import { useToast } from '@chakra-ui/react';
import { QueryKey, useQuery } from '@tanstack/react-query';
import { AxiosError, AxiosInstance } from 'axios';
import { useAxios } from 'context/AxiosContextProvider';
import { manageErrorResponse } from 'helpers/manageErrorResponse';
import { useEffect } from 'react';

import { PaginatedTasks } from '../api/types';
import config from '../config';

interface TasksQueryParams {
  searchString?: string;
  page?: number;
  itemsPerPage?: number;
  status?: Array<string>;
  type?: string;
  assignee?: number;
  startDate?: string;
  endDate?: string;
  sort?: string;
  order?: string;
  priority?: number[] | null;
}

interface GetTasks extends TasksQueryParams {
  axios: AxiosInstance;
}

async function getTasks({
  axios,
  searchString,
  page,
  itemsPerPage,
  status = [],
  type,
  assignee,
  startDate,
  endDate,
  sort,
  order,
  priority,
}: GetTasks): Promise<PaginatedTasks> {
  const { data } = await axios.get(`${config.apiBaseUrl}/op/v1/task`, {
    params: {
      page,
      itemsPerPage,
      status: status.length > 0 ? status?.join() : undefined,
      priority: priority && priority.length > 0 ? priority.join() : undefined,
      ...(searchString && { searchString }),
      ...(type && { type }),
      ...((assignee || assignee === 0 || assignee === null) && { userId: assignee }),
      ...(startDate && { from: startDate }),
      ...(endDate && { to: endDate }),
      ...(sort && { sort }),
      ...(order && { order }),
    },
  });
  return data;
}

export default function useTasksQuery({
  searchString,
  page,
  itemsPerPage,
  status,
  type,
  assignee,
  startDate,
  endDate,
  sort,               
  order,
  priority,
}: TasksQueryParams) {
  const axios = useAxios();
  const toast = useToast();

  const query = useQuery<PaginatedTasks, AxiosError<{ message: string }>>({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: getKey({
      searchString,
      page,
      itemsPerPage,
      status,
      type,
      assignee,
      startDate,
      endDate,
      sort,
      order,
      priority,
    }),
    queryFn: () =>
      getTasks({
        axios,
        searchString,
        page,
        itemsPerPage,
        status,
        type,
        assignee,
        startDate,
        endDate,
        sort,
        order,
        priority
      }),
    notifyOnChangeProps: ['data', 'isFetching', 'error'],
    retry: false,
  });

  useEffect(() => {
    if (query.isError) {
      manageErrorResponse(toast, query.error as any);
    }
  }, [query.error, query.isError, toast]);

  return query;
}

export function getKey({
  searchString,
  page,
  itemsPerPage,
  status,
  type,
  assignee,
  startDate,
  endDate,
  sort,
  order,
  priority,
}: {
  searchString?: string;
  page?: number;
  itemsPerPage?: number;
  status?: Array<string>;
  type?: string;
  assignee?: number;
  startDate?: string;
  endDate?: string;
  sort?: string;
  order?: string;
  priority?: number[] | null;
}) {
  return ['Tasks', searchString, page, itemsPerPage, status, type, assignee, startDate, endDate, sort, order, priority] as const satisfies QueryKey;
}
